import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  groupBy,
  forEach,
} from 'lodash';
import {
  withStyles,
  Icon,
  LinearProgress,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Tooltip,
  IconButton,
  Menu,
  MenuList,
  MenuItem,
  ListSubheader,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';

import { getEventTypes } from '@pingum/app-state-management/eventType/actions';
import {
  getUserSubscription,
  updateUserSubscription,
} from '@pingum/app-state-management/userSubscription/actions';
import { UserService } from '@pingum/api-services';

import SlackNotificationsModal from '../SlackNotificationsModal';
import eventTypeGroups from './eventTypeGroups';
import styles from './styles';

import { getTenantFromUrl } from '../../../../utils/urlHelper';
import FieldDisplayMessage from '../../../widgets/FieldDisplayMessage';
import SlackIcon from '../../../widgets/SlackIcon';

export const UI_DELIVERY_METHOD = 'ui';
export const EMAIL_DELIVERY_METHOD = 'email';
export const MOBILE_DELIVERY_METHOD = 'mobile';
export const SLACK_DELIVERY_METHOD = 'slack';

export const userHasRoles = (groupRoles, userRoles) => {
  if (groupRoles.length === 0) {
    return true;
  }
  let userHasRole = false;
  forEach(groupRoles, (groupRole) => {
    if (userRoles.indexOf(groupRole) > -1) {
      userHasRole = true;
    }
  });
  return userHasRole;
};

export const getUserSubscriptionName = (userSubscription) => {
  let name = 'Unknown';
  let icon = 'help';
  if (userSubscription.type === UI_DELIVERY_METHOD) {
    name = 'In App';
    icon = 'notifications';
  } else if (userSubscription.type === EMAIL_DELIVERY_METHOD) {
    name = 'Email';
    icon = 'email';
  } else if (userSubscription.type === MOBILE_DELIVERY_METHOD) {
    name = userSubscription.deviceName;
    icon = 'smartphone';
  } else if (userSubscription.type === SLACK_DELIVERY_METHOD) {
    name = 'Slack';
    icon = 'slack';
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <span>
        {
          icon === 'slack'
            ? <SlackIcon fontSize={18} margin="0 4px 4px 0" />
            : (
              <Icon
                style={{
                  fontSize: 18,
                  margin: '0 4px 4px 0',
                  color: grey[500],
                }}
              >
                {icon}
              </Icon>
            )
        }
      </span>
      {name}
    </div>
  );
};

export const getEventTypeNotificationComponent
  = (eventType, subscription, index, loading, handleOptOut, handleOptIn) => {
    if (!subscription) {
      // does not exist
      return (<span />);
    }
    if (subscription.optOuts.length === 0 || subscription.optOuts.indexOf(eventType) < 0) {
      return (
        <Checkbox
          color="primary"
          disabled={loading}
          checked
          onChange={
          () => handleOptOut(subscription, index, eventType)
        }
        />
      );
    }
    return (
      <Checkbox
        color="primary"
        disabled={loading}
        checked={false}
        onChange={
        () => handleOptIn(subscription, index, eventType)
      }
      />
    );
  };

export class EventTypesCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      showSlackNotificationsModal: false,
    };

    this.props.getUserSubscription(this.props.profile.user_id);
    this.props.getEventTypes();
  }

  handleOptOut = (subscription, index, eventType) => {
    if (!subscription || subscription.optOuts.indexOf(eventType) >= 0) {
      return; // already in list
    }

    subscription.optOuts.push(eventType);
    this.props.userSubscription.subscriptions[index] = subscription;

    this.props.updateUserSubscription(this.props.profile.user_id, this.props.userSubscription);
  }

  handleOptIn = (subscription, index, eventType) => {
    const updateSubscription = this.props.userSubscription.subscriptions[index];

    if (!updateSubscription || updateSubscription.optOuts.indexOf(eventType) < 0) {
      // already removed from list
      return;
    }

    updateSubscription.optOuts = updateSubscription.optOuts.filter((optOut) => optOut !== eventType);
    this.props.userSubscription.subscriptions[index] = updateSubscription;

    this.props.updateUserSubscription(this.props.profile.user_id, this.props.userSubscription);
  }

  handleToggleDisableNotifications = (event) => {
    this.props.userSubscription.disableNotifications = event.target.checked;
    this.props.updateUserSubscription(this.props.profile.user_id, this.props.userSubscription);
  }

  handleToggleReceiveMyActions = (event) => {
    this.props.userSubscription.receiveMyActions = event.target.checked;
    this.props.updateUserSubscription(this.props.profile.user_id, this.props.userSubscription);
  }

  handleMoreActionsClick = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  }

  handleMoreActionsClose = () => {
    this.setState({ anchorEl: null });
  }

  toggleSlackNotificationsModal = () => {
    this.setState((prevState) => ({
      showSlackNotificationsModal: !prevState.showSlackNotificationsModal,
      anchorEl: null,
    }));
  }

  render() {
    const {
      classes,
      isLoadingEventTypes,
      isLoadingSubscription,
      userSubscription,
      profile,
      updateUserSubscription: updateUserSubscriptionFunc,
    } = this.props;
    const {
      anchorEl,
      showSlackNotificationsModal,
    } = this.state;

    const userRoles = UserService.getUserRolesForTenant(
      this.props.profile,
      getTenantFromUrl(),
    );
    const eventTypes = groupBy(this.props.eventTypes, 'group');

    return (
      <Card>
        <CardHeader
          title="Delivery Preferences"
          action={(
            <div className={this.props.classes.cardHeaderIcons}>
              <Tooltip title="More Actions" id="notifications-more-actions-tooltip">
                <IconButton
                  onClick={this.handleMoreActionsClick}
                  className="more-actions-button"
                >
                  <Icon>more_vert</Icon>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={this.handleMoreActionsClose}
              >
                <ListSubheader>
                  More actions
                </ListSubheader>
                <MenuList>
                  <MenuItem
                    onClick={this.toggleSlackNotificationsModal}
                    button
                    className="slack-action-item"
                  >
                    <IconButton disableRipple>
                      <SlackIcon fontSize={24} />
                    </IconButton>
                    <ListItemText primary="Slack Notifications" />
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>
          )}
        />
        <CardContent>
          {
            (userSubscription.subscriptions
            && userSubscription.subscriptions.filter((subscription) => subscription.type === MOBILE_DELIVERY_METHOD).length === 0)
            && (
              <div>
                <FieldDisplayMessage
                  title="Pingum Mobile App"
                  subTitle="Download the Pingum mobile application from the app store to receive push notification right to your device"
                  icon="smartphone"
                />
                <Divider style={{ marginBottom: '1rem' }} />
              </div>
            )
          }

          {
            userSubscription.disableNotifications
            && (
              <Typography variant="h5" color="error" id="no-notifications-warning" gutterBottom>
                You currently are not receiving any notifications.
                Turn on notifications to start receiving them again.
              </Typography>
            )
          }
          <List>
            <ListItem>
              <ListItemText secondary="Disable all notifications" />
              <ListItemSecondaryAction>
                <Checkbox
                  disabled={isLoadingSubscription}
                  checked={userSubscription.disableNotifications}
                  onChange={this.handleToggleDisableNotifications}
                  value="disableNotifications"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemText secondary="Receive notifications for actions I perform" />
              <ListItemSecondaryAction>
                <Checkbox
                  disabled={isLoadingSubscription}
                  checked={userSubscription.receiveMyActions}
                  onChange={this.handleToggleReceiveMyActions}
                  value="receiveMyActions"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>

          {
            eventTypeGroups.filter((group) => userHasRoles(group.roles, userRoles)).map(({
              key,
              displayName,
              description,
            }) => (
              <div key={`event-type-group-${key}`} className={classes.notificationsContainer}>
                <Typography
                  variant="h3"
                  gutterBottom
                >
                  {displayName}
                </Typography>
                <Typography gutterBottom component="div">
                  {description}
                </Typography>
                <Table className={classes.notificationTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {
                        userSubscription.subscriptions
                         && userSubscription.subscriptions.map((subscription) => (
                           <TableCell
                             key={subscription.type + subscription.deviceId}
                             className={classes.tableHeaderCell}
                           >
                             <Typography
                               variant="h4"
                               gutterBottom
                             >
                               {getUserSubscriptionName(subscription)}
                             </Typography>
                           </TableCell>
                         ))
                      }
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      (eventTypes[key] || []).map((eventType) => (
                        <TableRow key={eventType.eventType}>
                          <TableCell className="event-type-row">
                            <Typography variant="body2" gutterBottom>
                              {eventType.displayName}
                            </Typography>
                          </TableCell>

                          {
                            userSubscription.subscriptions
                            && userSubscription.subscriptions.map((s, index) => (
                              <TableCell key={s.type + s.deviceId} className={classes.checkboxCell}>
                                {getEventTypeNotificationComponent(
                                  eventType.eventType,
                                  s,
                                  index,
                                  isLoadingSubscription,
                                  this.handleOptOut,
                                  this.handleOptIn,
                                )}
                              </TableCell>
                            ))
                          }
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </div>
            ))
          }
          {
            isLoadingEventTypes
              && <LinearProgress />
          }
        </CardContent>

        <SlackNotificationsModal
          isOpen={showSlackNotificationsModal}
          closeSlackNotificationsModal={this.toggleSlackNotificationsModal}
          updateUserSubscription={updateUserSubscriptionFunc}
          userId={profile.user_id}
          userSubscription={userSubscription}
        />
      </Card>
    );
  }
}

EventTypesCardComponent.propTypes = {
  classes: PropTypes.object,
  profile: PropTypes.object,
  getEventTypes: PropTypes.func,
  getUserSubscription: PropTypes.func,
  updateUserSubscription: PropTypes.func,
  eventTypes: PropTypes.array,
  isLoadingEventTypes: PropTypes.bool,
  isLoadingSubscription: PropTypes.bool,
  userSubscription: PropTypes.object,
};

EventTypesCardComponent.defaultProps = {
  classes: {},
  profile: { user_id: '' },
  getEventTypes: () => {},
  getUserSubscription: () => {},
  updateUserSubscription: () => {},
  eventTypes: [],
  isLoadingEventTypes: false,
  isLoadingSubscription: false,
  userSubscription: {
    disableNotifications: false,
    receiveMyActions: false,
  },
};

export const mapStateToProps = (state) => ({
  profile: state.user.user,
  isLoadingEventTypes: state.eventType.isFetchingEventTypes,
  isLoadingSubscription: state.userSubscription.isFetchingUserSubscription
    || state.userSubscription.isEditingUserSubscription,
  eventTypes: state.eventType.eventTypes,
  userSubscription: state.userSubscription.userSubscription,
});

export const mapDispatchToProps = (dispatch) => ({
  getEventTypes: () => dispatch(getEventTypes()),
  getUserSubscription: (auth0userId) => dispatch(getUserSubscription(auth0userId)),
  updateUserSubscription: (auth0userId, request) => dispatch(updateUserSubscription(auth0userId, request)),
});

export default withStyles(styles)(connect(
  mapStateToProps,
  mapDispatchToProps,
)(EventTypesCardComponent));
